import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  usePrevious,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { commitmentCreateSchema } from "features/commitments/CommitmentSchemas";
import {
  getClosingStatsRetrieveQueryKey,
  useClosingSecurityOwnershipCreate,
} from "gen/endpoints/closing/closing";
import { getCommitmentInformationTableDataRetrieveQueryKey } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { useCreditFacilityListSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useCreditFacilityBorrowingBaseGroupEntityIdRetrieveSuspense } from "gen/endpoints/credit-facility-borrowing-base-by-entity-id/credit-facility-borrowing-base-by-entity-id";
import { useFundList } from "gen/endpoints/fund-entities/fund-entities";
import { useSecurityClassList } from "gen/endpoints/fund-security-classes/fund-security-classes";
import { useInvestorClassList } from "gen/endpoints/investor-class/investor-class";
import { useInvestorEntityList } from "gen/endpoints/investor-entity/investor-entity";
import { ClosingData } from "gen/model/closingData";
import { SecurityOwnershipCreateDataRequest } from "gen/model/securityOwnershipCreateDataRequest";
import { SecurityOwnershipReadData } from "gen/model/securityOwnershipReadData";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect, Select } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { getFieldError } from "shared/utils/FormUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

interface AddCommitmentModalProps {
  fundFamilyId: string;
  closing: ClosingData;
  onCloseComplete: () => void;
}

interface AdvanceRateGroupTextProps {
  owningEntityId: string;
  fundFamilyId: string;
}

interface AddCommitmentFormProps {
  fundFamilyId: string;
  onSubmit: (values: SecurityOwnershipReadData) => Promise<void>;
  onSuccess: () => void;
  onCancel: () => void;
}

const AdvanceRateGroupText: React.FC<AdvanceRateGroupTextProps> = ({
  fundFamilyId,
  owningEntityId,
}) => {
  const { t } = useTranslation(["creditFacility"]);

  const { data: borrowingBaseGroup } =
    useCreditFacilityBorrowingBaseGroupEntityIdRetrieveSuspense(
      fundFamilyId,
      owningEntityId,
    );

  return (
    <Text marginBottom={4} fontSize={12} fontWeight={400}>
      {t("creditFacility:borrowingBase.commitmentClosing.advanceRateGroup", {
        borrowingBaseName: borrowingBaseGroup.name,
      })}
      <Tooltip
        key="tooltip"
        label={t("creditFacility:borrowingBase.commitmentClosing.message")}
        placement="top"
      >
        <InfoCircleIcon w="12px" h="12px" ml="10px" color="gray.400" />
      </Tooltip>
    </Text>
  );
};

const AddCommitmentForm: React.FC<AddCommitmentFormProps> = ({
  fundFamilyId,
  onSubmit,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation([
    "commitment",
    "common",
    "commitmentClass",
    "creditFacility",
  ]);

  const { data: investors } = useInvestorEntityList();
  const { data: funds } = useFundList({ fund_family_ids: [fundFamilyId] });

  const [fundId, setFundId] = useState<string>("");
  const prevFundId = usePrevious(fundId);
  const fundFilter = fundId
    ? {
        entity_id: fundId,
      }
    : {};

  const { data: securityClasses } = useSecurityClassList(
    fundFamilyId,
    fundFilter,
  );

  const { data: investorClasses, isLoading: isLoadingClasses } =
    useInvestorClassList(fundFamilyId);

  const form = useForm<SecurityOwnershipReadData>({
    // @ts-expect-error TODO: Convert to zod
    resolver: yupResolver(commitmentCreateSchema),
  });

  const owningEntityId = form.watch("owning_entity_id");

  const isLoading = form.formState.isSubmitting;

  useEffect(() => {
    // TODO ENG-914: Sort out how to handle setting security class for a commitment
    // assuming that there is always a default security class for the entity for now!
    if (securityClasses && securityClasses.length > 0) {
      form.setValue("security_class_id", securityClasses[0].id);
      form.setValue("owned_entity_id", fundId);
    }
    if (fundId !== prevFundId) {
      form.resetField("investor_class_id");
    }
  }, [form, fundId, prevFundId, securityClasses]);

  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);
  const creditFacilityId = creditFacilityList[0]?.id;

  return (
    <Form
      form={form}
      successMessage={t("commitment:add.success")}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      // Prevent errors for conditionally rendered fields from being included
      // in the toast message generated by getFormNonFieldErrorMessage.
      excludeNonFieldErrors={[
        "owned_entity_id",
        "owning_entity_id",
        "investor_class_id",
      ]}
    >
      <ModalBody>
        <FormSelect
          name="owning_entity_id"
          title={t("common:investor")}
          isDisabled={!investors?.length}
        >
          {investors?.map(({ id, legal_name }) => (
            <SelectOption key={`investor_${id}`} value={id!.toString()}>
              {legal_name}
            </SelectOption>
          ))}
        </FormSelect>
        {owningEntityId && creditFacilityId && (
          <AdvanceRateGroupText
            fundFamilyId={fundFamilyId}
            owningEntityId={owningEntityId}
          />
        )}
        <FormMoneyInput
          name="commitment_amount"
          title={t("commitment:commitmentAmount")}
        />
        <Select
          title={t("common:fund")}
          value={fundId}
          onChangeValue={setFundId}
          name="owned_entity_id"
          isDisabled={!funds?.length || isLoading}
          error={
            getFieldError(form.formState.errors, "owned_entity_id")?.message
          }
        >
          {funds?.map(({ id, legal_name }) => (
            <SelectOption key={`fund_${id}`} value={id.toString()}>
              {legal_name}
            </SelectOption>
          ))}
        </Select>
        {fundId && (
          <FormSelect
            name="investor_class_id"
            title={t("commitmentClass:class")}
            isDisabled={isLoadingClasses}
          >
            {investorClasses?.map(({ id, name }) => (
              <SelectOption key={`class_${id}`} value={id.toString()}>
                {name}
              </SelectOption>
            ))}
          </FormSelect>
        )}
      </ModalBody>
      <ModalFooter gap={2}>
        <ButtonOrder
          dismiss={<FormCancelButton onClick={onCancel} />}
          confirm={
            <FormSubmitButton>{t("commitment:add.title")}</FormSubmitButton>
          }
        />
      </ModalFooter>
    </Form>
  );
};

const AddCommitmentModal: React.FC<AddCommitmentModalProps> = ({
  fundFamilyId,
  closing,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["commitment"]);
  const queryClient = useQueryClient();

  const { mutateAsync: createClosingCommitment } =
    useClosingSecurityOwnershipCreate();

  const { value: isOpen, setFalse: close } = useBoolean(true);

  const onSubmit = useCallback(
    async (data: SecurityOwnershipReadData) => {
      const createData: SecurityOwnershipCreateDataRequest = {
        ...data,
        fund_family_id: fundFamilyId,
      };
      await createClosingCommitment({
        fundFamilyId,
        closingId: closing.id!,
        data: {
          security_ownership_data: createData,
          security_ownership_allocation_overrides: [],
        },
        params: {
          version_date: closing.closing_date,
        },
      });
      await invalidateQueryKeys(
        queryClient,
        getCommitmentInformationTableDataRetrieveQueryKey({
          fund_family_id: fundFamilyId,
          closing_id: closing.id,
        }),
        getCommitmentInformationTableDataRetrieveQueryKey({
          fund_family_id: fundFamilyId,
          is_unattached_to_closing: true,
        }),
        getClosingStatsRetrieveQueryKey(fundFamilyId, closing.id!),
      );
    },
    [
      closing.id,
      closing.closing_date,
      createClosingCommitment,
      fundFamilyId,
      queryClient,
    ],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("commitment:add.title")}</ModalHeader>
        <ModalCloseButton />
        <QueryBoundary>
          <AddCommitmentForm
            fundFamilyId={fundFamilyId}
            onSubmit={onSubmit}
            onSuccess={close}
            onCancel={close}
          />
        </QueryBoundary>
      </ModalContent>
    </Modal>
  );
};

export default AddCommitmentModal;
